<template>
  <AppContainer flush-header>
    <div>
      <div class="bg-surface-muted border-surface-border -mx-4 space-y-2 border-b px-6 py-4">
        <div class="flex justify-between">
          <div>#{{ workOrder?.id }}</div>
          <EStatus :label="workOrder?.state_name" :variant="workOrder?.statusVariant" />
        </div>
        <h1 class="mb-4 text-xl font-bold">{{ workOrder?.summary }}</h1>

        <EActionGroup
          :label-more="t(useTranslationPath(callerPath, 'more'))"
          :items="navigationActionItems"
        />

        <ETabsHorizontal v-model="tab" class="site-detail-tabs">
          <ETabsHorizontalItem id="details" :label="t(useTranslationPath(callerPath, 'details'))" />
          <ETabsHorizontalItem id="assets" :label="t(useTranslationPath(callerPath, 'assets'))" />
          <ETabsHorizontalItem id="updates" :label="t(useTranslationPath(callerPath, 'updates'))" />
        </ETabsHorizontal>
      </div>

      <div class="py-4">
        <div v-if="tab === 'details'" class="space-y-4">
          <EList>
            <EListItem
              v-if="workOrder?.due_date"
              :title="t(useTranslationPath(callerPath, 'dueBy'))"
              :description="useDayJs(workOrder?.due_date).format('L')"
            />
            <EListItem :title="t(useTranslationPath(callerPath, 'workDetails'))">
              {{ workOrder?.description }}
            </EListItem>
          </EList>

          <template v-if="workOrder?.media?.length">
            <div class="text-lg font-bold">
              {{ t(useTranslationPath(callerPath, 'attachments')) }}
            </div>
            <EList>
              <EListItem
                v-for="(document, key) in workOrder?.media"
                :key="`document-${key}`"
                :title="document.name"
                :description="getFileType(document.file_name)"
              >
                <template #action>
                  <EButton
                    :label="t(useTranslationPath(callerPath, 'download'))"
                    icon="download"
                    icon-only
                    variant="secondary"
                    @click="downloadFile(document)"
                  />
                </template>
              </EListItem>
            </EList>
          </template>

          <EList>
            <EListItem
              v-if="!workOrder?.site_hide_access_times"
              :title="t(useTranslationPath(callerPath, 'siteAccessTimes'))"
            >
              <EListItemProperty
                v-for="(access, index) in accessTimes"
                :key="index"
                :label="access.day"
                :value="access.time"
              />
            </EListItem>
            <EListItem
              v-if="workOrder?.site_access_instructions"
              :title="t(useTranslationPath(callerPath, 'siteAccessInstructions'))"
              :description="workOrder?.site_access_instructions"
            />
            <EListItem
              :title="t(useTranslationPath(callerPath, 'siteTelephone'))"
              :description="
                workOrder?.site_telephone ?? t(useTranslationPath(callerPath, 'notAvailable'))
              "
            >
              <template v-if="workOrder?.site_telephone" #action>
                <EButton
                  :label="t(useTranslationPath(callerPath, 'call'))"
                  icon="phone"
                  icon-only
                  variant="secondary"
                  @click="callNumber"
                />
              </template>
            </EListItem>
            <EListItem
              :title="t(useTranslationPath(callerPath, 'siteAddress'))"
              :description="
                workOrder?.site_address ?? t(useTranslationPath(callerPath, 'notAvailable'))
              "
            />
            <EListItem
              :title="t(useTranslationPath(callerPath, 'siteLocation'))"
              :description="
                workOrder?.primary_location_path ??
                t(useTranslationPath(callerPath, 'notAvailable'))
              "
            />
            <EListItem
              :title="t(useTranslationPath(callerPath, 'siteNotes'))"
              :description="
                workOrder?.primary_location_notes ??
                t(useTranslationPath(callerPath, 'notAvailable'))
              "
            />
            <EListItem
              :title="t(useTranslationPath(callerPath, 'pointOfContact'))"
              :description="
                workOrder?.point_of_contact ?? t(useTranslationPath(callerPath, 'notAvailable'))
              "
            />
          </EList>

          <template v-if="siteDocuments?.length">
            <div class="text-lg font-bold">
              {{ t(useTranslationPath(callerPath, 'siteDocuments')) }}
            </div>
            <EList>
              <EListItem
                v-for="(document, key) in siteDocuments"
                :key="`document-${key}`"
                :title="document.file_name"
                class="cursor-pointer"
                @click="downloadFile(document)"
              >
                <EListItemProperty
                  :label="t(useTranslationPath(callerPath, 'documentName'))"
                  :value="document.name"
                />
                <EListItemProperty
                  :label="t(useTranslationPath(callerPath, 'documentType'))"
                  :value="document.document_type_name"
                />
                <EListItemProperty
                  v-if="document.valid.from"
                  :label="t(useTranslationPath(callerPath, 'validFrom'))"
                  :value="useDayJs(document.valid.from).format('L')"
                />
                <EListItemProperty
                  v-if="document.valid.until"
                  :label="t(useTranslationPath(callerPath, 'validUntil'))"
                  :value="useDayJs(document.valid.until).format('L')"
                />
              </EListItem>
            </EList>
          </template>
        </div>
        <div v-else-if="tab === 'assets'">
          <Assets :assets="workOrder?.assets" />
        </div>
        <div v-else-if="tab === 'updates'">
          <Updates :work-order-id="workOrder?.id" />
        </div>
      </div>
    </div>
  </AppContainer>
</template>

<script setup>
import AppContainer from '@/components/AppContainer.vue'
import Assets from '@/components/WorkOrder/Assets.vue'
import Updates from '@/components/WorkOrder/Updates.vue'
import { useDownloadFile } from '@/composables/useDownloadFile.js'
import { useGetConfigVar } from '@/composables/useGetConfigVar.js'
import { useGoBack } from '@/composables/useGoBack.js'
import { usePreviousRoute } from '@/composables/usePreviousRoute.js'
import { useRedirectToError } from '@/composables/useRedirectToError.js'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { isLocationEnabled } from '@/composables/useVerificationMethod.js'
import { EngineerSessionLocationVerificationMethod } from '@/enums/EngineerSessionLocationVerificationMethod.js'
import { useAppStore } from '@/stores/AppStore.js'
import { useEngineerStore } from '@/stores/authenticated/EngineerStore.js'
import { useSiteStore } from '@/stores/authenticated/SiteStore.js'
import { useWorkOrderStore } from '@/stores/authenticated/WorkOrderStore'
import { getFileType } from '@/utils/getFileType.js'
import { getRelationshipCountForItem, useDayJs } from '@expansivefm/api-request-parser'
import {
  EActionGroup,
  EButton,
  EList,
  EListItem,
  EListItemProperty,
  EStatus,
  ETabsHorizontal,
  ETabsHorizontalItem,
} from '@expansivefm/eds'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { downloadFile } = useDownloadFile()

const appStore = useAppStore()
const siteStore = useSiteStore()
const engineerStore = useEngineerStore()
const workOrderStore = useWorkOrderStore()

const { setCustomGoBack } = useGoBack()

const callerPath = 'views.authenticated.workOrders.detail'

const tab = ref('details')

const navigationActionItems = computed(() => [
  {
    label: t(useTranslationPath(callerPath, 'manageEngineers')),
    icon: 'users',
    click: handleManagerEngineers,
  },
  {
    label: t(useTranslationPath(callerPath, 'checkOut')),
    icon: 'arrow-right-from-bracket',
    click: handleCheckout,
  },
])

const workOrder = computed(() => workOrderStore.workOrder)
const site = computed(() => siteStore.site)

const accessTimes = computed(() => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  return workOrder.value?.site_access_times?.map((time, index) => {
    const day = days[index]
    return {
      day,
      time: time ? `${time.from} - ${time.to}` : t(useTranslationPath(callerPath, 'closed')),
    }
  })
})

const siteDocuments = computed(() => {
  const documents = []

  if (site.value.documents?.length === 0) {
    return documents
  }

  site.value.documents?.forEach((document) => {
    document.media?.forEach((media) => {
      documents.push({
        name: document.name,
        document_type_name: document.document_type_name,
        valid: { from: document.valid_from, until: document.valid_until },
        file_name: media.file_name,
        mime_type: media.mime_type,
        url: media.url,
      })
    })
  })

  return documents
})

onMounted(async () => {
  if (
    Object.keys(engineerStore.session).length === 0 &&
    engineerStore.previousActiveSessions.length > 0
  ) {
    await router.push({ name: 'sessions' })
  }

  if (workOrderStore.workOrder?.id !== parseInt(route.params.workOrderId)) {
    await useRedirectToError(router)
      .setTitle(t(useTranslationPath(callerPath, 'mismatchErrorTitle')))
      .setMessage(t(useTranslationPath(callerPath, 'mismatchErrorMessage')))
      .setStatus(404)
      .redirect()
  }

  appStore.$patch({ showBackButton: false })

  await workOrderStore
    .fetchWorkOrder(workOrder.value?.id, ['media', 'assets', 'documents', 'documents.media'])
    .then(async ({ data: workOrderData }) => {
      await siteStore.fetchSite(workOrderData.site_maintainable_entity_id, [
        'documents',
        'documents.media',
      ])

      workOrderStore.$patch({ workOrder: workOrderData })
    })

  await engineerStore
    .getSession(
      engineerStore.session.id,
      {
        'engineer-sessions': ['activeEngineers'],
        users: ['name'],
      },
      ['activeEngineers'],
      ['activeEngineers'],
    )
    .then(({ data, meta }) => {
      engineerStore.$patch({
        activeEngineerCount: data
          ? getRelationshipCountForItem(meta, data.id, 'activeEngineers')
          : 0,
      })
    })

  if (!['sessions'].includes(usePreviousRoute(router).previousRoute.value?.name)) {
    setCustomGoBack(() =>
      router.push({
        name: 'work-order.index',
      }),
    )

    appStore.$patch({ showBackButton: true })
  }
})

const handleManagerEngineers = () => {
  router.push({
    name: 'work-order.manage-engineers',
    params: {
      workOrderId: workOrderStore.workOrder?.id,
    },
  })
}

const handleCheckout = async () => {
  if (
    useGetConfigVar('ENGINEER_APP_CHECK_OUT_METHOD') ===
    EngineerSessionLocationVerificationMethod.ACCESS_CODE
  ) {
    await router.push({
      name: 'work-order.checkout.verification.code',
      params: {
        workOrderId: workOrderStore.workOrder?.id,
      },
    })

    return
  }

  if (isLocationEnabled('ENGINEER_APP_CHECK_OUT_METHOD')) {
    await router.push({
      name: 'work-order.checkout.verification.geo',
      params: {
        workOrderId: workOrderStore.workOrder?.id,
      },
    })

    return
  }

  await router.push({
    name: 'work-order.checkout.index',
    params: {
      workOrderId: workOrderStore.workOrder?.id,
    },
  })
}

const callNumber = () => {
  const phoneNumber = workOrder.value?.site_telephone
  if (phoneNumber) {
    window.location.href = `tel:${phoneNumber}`
  }
}
</script>
